"use client";

import { useMemo } from "react";
import { createSeededRNG } from "../../utils/random";
import { useCountWithInterval, useTick } from "./hooks";
import { tween } from "./utils";
export function CipherText({
  children: text,
  active = true,
  paused = false,
  charSet = "-_~`!@#$%^&*()+=[]{}|;:,.<>?",
  interval: intervalMs = 50,
  infinite = false,
  progress: forcedProgress,
  initialProgress = 0
}) {
  const size = text.length;

  /**
   * Progress for the text layer
   */
  const autoProgress = useCountWithInterval(initialProgress, {
    max: size,
    paused,
    increase: active,
    intervalMs
  });
  const progress = forcedProgress ?? autoProgress;

  /**
   * Progress for the infinite layer
   */
  const infiniteProgress = useCountWithInterval(infinite ? size : 0, {
    max: size,
    increase: infinite,
    intervalMs
  });
  useTick(infiniteProgress > 0, intervalMs);

  /**
   * Determining layers
   */
  const random = useMemo(() => createSeededRNG(text), [text]);
  const percentComplete = progress / size;
  const baseRandomLayer = new Array(size).fill("");
  const layers = [
  // Base text layer
  {
    text,
    pattern: new Array(size).fill(false).map((_, i) => progress > i)
  },
  // Cipher layer
  {
    text: baseRandomLayer.map(() => charSet[Math.floor(random() * charSet.length)]),
    pattern: baseRandomLayer.map((__0, i_0) => progress > i_0 ? random() > tween(0.25, 1, percentComplete) : false)
  },
  // Infinite layer (just random cipher if enabled)
  infiniteProgress > 0 ? {
    text: baseRandomLayer.map(() => charSet[Math.floor(random() * charSet.length)]),
    pattern: new Array(size).fill(false).map(() => infiniteProgress / size > random())
  } : undefined].filter(Boolean);

  /**
   * Merging layers into a single string
   */
  const mergedLayers = useMemo(() => {
    const text_0 = new Array(size).fill("");
    for (const layer of layers) {
      for (let i_1 = 0; i_1 < layer.text.length; i_1++) {
        if (layer.pattern?.[i_1] === false) {
          continue;
        }
        text_0[i_1] = layer.text[i_1];
      }
    }
    return text_0.join("").trim();
  }, [layers, size]);
  return <>{mergedLayers}</>;
}